import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { fmtTL } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, EyeOff, Eye } from "lucide-react";
import { ExcelTools } from "@/components/ExcelTools";

export const PRODUCT_UNITS = ["KG", "ADET", "BOY", "TABA", "MT"];
const blank = { name: "", code: "", category: "", size: "", unit: "ADET", purchase_price: "", sale_price: "", stock: "", min_stock: "", active: true, note: "" };

export default function Products() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [q, setQ] = useState("");
  const [showPassive, setShowPassive] = useState(false);
  const [form, setForm] = useState(blank);

  const load = async () => setItems((await api.get("/products")).data);
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditId(null); setForm(blank); setOpen(true); };
  const openEdit = (p) => {
    setEditId(p.id);
    setForm({ ...blank, ...p, purchase_price: p.purchase_price ?? "", sale_price: p.sale_price ?? "", stock: p.stock ?? "", min_stock: p.min_stock ?? "", unit: (p.unit || "ADET").toUpperCase(), active: p.active !== false });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) return toast.error("Ürün adı gerekli");
    const body = {
      ...form,
      purchase_price: Number(form.purchase_price) || 0,
      sale_price: Number(form.sale_price) || 0,
      stock: Number(form.stock) || 0,
      min_stock: Number(form.min_stock) || 0,
      active: form.active !== false,
    };
    try {
      if (editId) { await api.put(`/products/${editId}`, body); toast.success("Ürün güncellendi"); }
      else { await api.post("/products", body); toast.success("Ürün eklendi"); }
      setOpen(false); setEditId(null); setForm(blank); load();
    } catch (err) { toast.error("Hata: " + (err.response?.data?.detail || err.message)); }
  };

  const togglePassive = async (p) => {
    try {
      await api.put(`/products/${p.id}`, { ...p, active: p.active === false });
      toast.success(p.active === false ? "Ürün aktifleştirildi" : "Ürün pasife alındı");
      load();
    } catch (err) { toast.error("İşlem başarısız"); }
  };

  const filtered = items
    .filter((p) => (showPassive || p.active !== false))
    .filter((p) => (p.name + " " + (p.code || "") + " " + (p.size || "")).toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Ürünler</h1>
          <p className="text-sm text-muted-foreground">{filtered.length} ürün</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ExcelTools kind="products" label="Ürünler" onImported={load} />
          <Button onClick={openNew} data-testid="new-product-btn"><Plus size={16} className="mr-1" /> Yeni Ürün</Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditId(null); setForm(blank); } }}>
        <DialogContent className="bg-popover max-w-lg">
          <DialogHeader><DialogTitle>{editId ? "Ürünü Düzenle" : "Yeni Ürün"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Ürün Kodu</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="örn: SAC-001" data-testid="product-code-input" /></div>
              <div><Label>Ürün Adı *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="örn: Sac Levha" data-testid="product-name-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Birim</Label>
                <Select value={PRODUCT_UNITS.includes(form.unit) ? form.unit : "ADET"} onValueChange={(v) => setForm({ ...form, unit: v })}>
                  <SelectTrigger data-testid="product-unit-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover">{PRODUCT_UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Varsayılan Satış Fiyatı (₺)</Label><Input type="number" step="0.01" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: e.target.value })} placeholder="0" data-testid="product-saleprice-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Ölçü (opsiyonel)</Label><Input value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} placeholder="örn: 1500x6000x5 mm" /></div>
              <div><Label>Kategori (opsiyonel)</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Serbest" /></div>
            </div>
            <Button type="submit" className="w-full" data-testid="product-submit-btn">{editId ? "Güncelle" : "Kaydet"}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <Input placeholder="Ürün / kod ara..." value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" data-testid="product-search" />
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
            <input type="checkbox" checked={showPassive} onChange={(e) => setShowPassive(e.target.checked)} data-testid="product-show-passive" /> Pasifleri göster
          </label>
        </div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Kod</TableHead><TableHead>Ürün</TableHead><TableHead>Ölçü</TableHead>
            <TableHead>Birim</TableHead><TableHead className="text-right">Satış Fiyatı</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map((p) => (
              <TableRow key={p.id} className={p.active === false ? "opacity-50" : ""}>
                <TableCell className="font-mono text-sm">{p.code || "-"}</TableCell>
                <TableCell className="font-semibold">
                  {p.name}
                  {p.active === false && <span className="ml-2 text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground">Pasif</span>}
                </TableCell>
                <TableCell className="font-mono text-sm">{p.size}</TableCell>
                <TableCell>{(p.unit || "").toUpperCase()}</TableCell>
                <TableCell className="text-right num font-semibold">{fmtTL(p.sale_price)}</TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`product-edit-${p.id}`} title="Düzenle"><Pencil size={14} /></Button>
                    {isAdmin && <Button variant="ghost" size="icon" onClick={() => togglePassive(p)} data-testid={`product-toggle-${p.id}`} title={p.active === false ? "Aktif Et" : "Pasife Al"}>{p.active === false ? <Eye size={14} /> : <EyeOff size={14} />}</Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Ürün yok</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
